<template>
  <div class="p-6 dark:text-gray-50"
       v-if="canAdd || currentUser?.is_staff"
    >
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex space-x-3">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Add New User</h1>
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="showHowToUser=true"
        >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark-icon lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
      </div>
      
      <button
        @click="$router.push('/users')"
        class="bg-gray-600 text-white btn hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
      >
        Back to Users
      </button>
    </div>

    <!-- Error Alert -->
    <div
      v-if="error"
      class="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md"
    >
      {{ error }}
    </div>

    <HowToUser v-if="showHowToUser"
      :external-navigation="true"
      :show-how-to-user="showHowToUser"
      @show-how-to-close="handleShowHowToUser"
    />

    <!-- User Form -->
    <form @submit.prevent="saveUser">
      <div class="p-6 space-y-6 glass-card">
        <!-- Basic Information -->
        <div>
          <h2 class="text-lg font-medium text-gray-900 dark:text-gray-50 mb-4">
            Basic Information
          </h2>
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label
                for="username"
                class="block text-sm font-medium text-gray-700 dark:text-gray-50"
              >
                Email (Username)
              </label>
              <input
                id="username"
                v-model="user.username"
                type="text"
                @focus="clearFieldError('username')"
                :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', validationErrors['username']
                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                  : 'focus:ring-blue-500'
                ]"
              />
              <FieldError :message="validationErrors['username']" />
            </div>

            <div>
              <label
                for="first_name"
                class="block text-sm font-medium text-gray-700 dark:text-gray-50"
              >
                First Name
              </label>
              <input
                id="first_name"
                v-model="user.first_name"
                type="text"
                class="h-10 mt-1 block w-full rounded-md border-gray-300 dark:bg-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                for="last_name"
                class="block text-sm font-medium text-gray-700 dark:text-gray-50"
              >
                Last Name
              </label>
              <input
                id="last_name"
                v-model="user.last_name"
                type="text"
                class="h-10 mt-1 block w-full rounded-md border-gray-300 dark:bg-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label for="org" class="block text-sm font-medium text-gray-700 dark:text-gray-50">
                Organisation
              </label>
              <GlassSelect
                id="org"
                v-model="user.org"
                @focus="clearFieldError('org')"
                :options="organizations"
                :searchable="true"
                placeholder="Select an organisation"
                :error="!!validationErrors['org']"
              />
              <FieldError :message="validationErrors['org']" />
            </div>
          </div>
        </div>

        <div v-if="selectedOrgIsServiceProvider" class="col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-50 mb-2">
            Assign Organisations
          </label>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Select which organisations this user can access
          </p>
          
          <div class="border border-gray-300 dark:border-gray-600 rounded-md max-h-60 overflow-y-auto p-3 space-y-2 bg-gray-50 dark:bg-gray-700">
            <div
              v-for="org in serviceProviderOrgs"
              :key="org.id"
              class="flex items-center"
            >
              <input
                :id="`org-${org.id}`"
                type="checkbox"
                :value="org.id"
                v-model="user.assigned_orgs"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label
                :for="`org-${org.id}`"
                class="ml-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                {{ org.name }}
              </label>
            </div>
            
            <div v-if="serviceProviderOrgs.length === 0" class="text-sm text-gray-500 dark:text-gray-400 italic">
              No client organisations found for this service provider
            </div>
          </div>
        </div>

        <!-- User Status -->
        <div>
          <h2 class="text-lg font-medium text-gray-900 dark:text-gray-50 mb-4">User Status</h2>
          <div class="space-y-4">
            <div class="flex items-start">
              <div class="flex h-5 items-center">
                <input
                  id="is_active"
                  v-model="user.is_active"
                  type="checkbox"
                  class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>
              <div class="ml-3">
                <label for="is_active" class="font-medium text-gray-700 dark:text-gray-50"
                  >Active</label
                >
                <p class="text-sm text-gray-500 dark:text-gray-50">
                  Whether this user should be treated as active.
                </p>
              </div>
            </div>

            <div>
              <label class="block font-medium text-gray-700 dark:text-gray-50">Role</label>
              <p class="text-sm text-gray-500 dark:text-gray-50 mb-2">
                The user's access level.
              </p>
              <div class="flex flex-wrap gap-x-6 gap-y-2">
                <label
                  v-for="opt in roleOptions"
                  :key="opt.value"
                  class="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="role"
                    :value="opt.value"
                    v-model="user.role"
                    class="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span class="text-sm text-gray-700 dark:text-gray-50">{{ opt.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div
        class="glass-card p-6 flex items-center justify-end space-x-3"
      >
        <button
          type="button"
          @click="$router.push('/users')"
          class="btn border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          :disabled="loading"
          class="btn border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {{ loading ? "Creating..." : "Create User" }}
        </button>
      </div>
    </form>
  </div>

  <!-- Error Message -->
  <PageError v-if="permsLoaded && !canAdd && !currentUser?.is_staff"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />
</template>

<script>
import { createUser } from "@/services/userService";
import { getAllOrgs } from "@/services/orgService";
import GlassSelect from "@/components/GlassSelect.vue";
import { useStore } from "@/store";
import PageError from "@/components/PageError.vue";
import { errorMessages } from "@/utils/errorMessages";
import HowToUser from "./HowToUser.vue";
import FieldError from "@/components/FieldError.vue";

export default {
  name: "AddUser",
  components: {
    PageError,
    HowToUser,
    FieldError,
    GlassSelect,
  },
  data() {
    return {
      showHowToUser: false,
      errorMessages: errorMessages,
      user: {
        username: "",
        first_name: "",
        last_name: "",
        is_active: true,
        role: "viewer",
        org: "",
        assigned_orgs: [],
      },
      canAdd: false,
      loading: false,
      error: null,
      organizations: [],
      validationErrors: {
        username: "",
        org: "",
      },
    };
  },
  computed: {
    currentUser(){
      return useStore.getUser();
    },
    roleOptions() {
      return [
        { value: "viewer", label: "Viewer" },
        { value: "editor", label: "Editor" },
        { value: "admin", label: "Admin" },
      ];
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },

    selectedOrgIsServiceProvider() {
      if (!this.user.org) return false;
      const org = this.organizations.find(o => o.id === parseInt(this.user.org));
      if (!org) return false;
      if (org.name.toLowerCase() === "splitpoint") return false;
      return org.is_service_provider || false;
    },
    
    serviceProviderOrgs() {
      if (!this.selectedOrgIsServiceProvider) return [];
      return this.organizations.filter(
        org => org.service_provider === parseInt(this.user.org)
      );
    }
  },
  async created() {
    await this.fetchOrganizations();
  },
  methods: {
    clearFieldError(fieldName){
      if (this.validationErrors[fieldName]) {
        delete this.validationErrors[fieldName];
        delete this.validationErrors['non_field_errors'];
      }
    },
    handleShowHowToUser(value){
      this.showHowToUser = value;
    },
    async checkPermissions() {
      if (this.currentUser) {
        this.canAdd = await useStore.hasPermission("Can add user");
      }
    },
    async fetchOrganizations() {
      try {
        const orgs = await getAllOrgs();
        this.organizations = orgs;
      } catch (error) {
        console.error("Error fetching organizations:", error);
        this.error = "An error occurred while fetching organizations.";
      }
    },
    validateForm() {
      let isValid = true;
      this.validationErrors = {
        username: "",
        org: "",
      };

      if (!this.user.username) {
        this.validationErrors.username = "Email is required";
        isValid = false;
      } else if (!this.user.username.includes("@")) {
        this.validationErrors.username = "Invalid email format";
        isValid = false;
      }

      if (!this.user.org) {
        this.validationErrors.org = "Organization is required";
        isValid = false;
      }

      return isValid;
    },
    async saveUser() {
      try {
        if (!this.validateForm()) {
          return;
        }

        this.loading = true;
        this.error = null;

        // Ensure org is a number
        const userData = {
          ...this.user,
          org: parseInt(this.user.org, 10),
          assigned_orgs: this.user.assigned_orgs.map(id => parseInt(id, 10)),
        };

        const newUser = await createUser(userData);
        this.$router.push("/users");
      } catch (error) {
        console.error("Error creating user:", error);
        if (error.response?.data) {
          // Handle specific backend validation errors
          const errors = error.response.data;
          if (typeof errors === "object") {
            Object.keys(errors).forEach((key) => {
              if (key in this.validationErrors) {
                this.validationErrors[key] = errors[key][0];
              }
            });
          }
          this.error = "Please correct the errors below";
        } else {
          this.error = "Failed to create user";
        }
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    "user.org"() {
      // Reset assigned orgs when the organisation changes.
      this.user.assigned_orgs = [];
    },
    currentUser: {
      handler(currentUser) {
        if (currentUser) {
          this.checkPermissions();
        }
      },
      immediate: true 
    },
  },
};
</script>
